import { useRoute, ref, computed } from '@nuxtjs/composition-api';
import { CancelToken } from 'axios';
import { orderBy } from 'lodash-es';
import { useStore } from '~/composables/useStore';
import { ECSchema } from '~/types/api';
import { PastArticle } from '~/types/modules/blog';
import { StaffDetail } from '~/types/modules/staff';
import { BlogUrlQueries } from '~/enums/blog';

export const useFilterBlogList = () => {
  const route = useRoute();
  const store = useStore();

  const parsedPastArticlesData = ref<PastArticle[]>([]);
  const staffList = ref<StaffDetail[]>([]);

  const selectedSortOption = ref<string>('');
  const selectedYearOption = ref<string>('');
  const selectedMonthOption = ref<string>('');

  const convertQueryToMap = () => {
    const { query } = route.value;

    if (query[BlogUrlQueries.SORT]) {
      selectedSortOption.value = query[BlogUrlQueries.SORT]?.toString();
    }

    if (query[BlogUrlQueries.YEAR]) {
      selectedYearOption.value = query[BlogUrlQueries.YEAR]?.toString();
    }

    if (query[BlogUrlQueries.MONTH]) {
      selectedMonthOption.value = query[BlogUrlQueries.MONTH]?.toString();
    }
  };

  const convertMapToQuery = () => {
    const query: any = {};
    query[BlogUrlQueries.SORT] = selectedSortOption.value || undefined;
    query[BlogUrlQueries.YEAR] = selectedYearOption.value || undefined;
    query[BlogUrlQueries.MONTH] = selectedMonthOption.value || undefined;
    return query;
  };

  const fetchPastArticles = async (shopId: string) => {
    const data = await store.dispatch('blog/master/fetchBlogPublishOptions', {
      shopId,
      listDisplayFlag: 1,
      detailDisplayFlag: 1,
      isCurrentlyPublished: true,
    });

    const parsedDataMap: any = {};

    // サーチのファセットで取得した発行年月データパース処理
    data.forEach((blog: any) => {
      const curYear = blog.year;
      const curMonth = blog.month.toString();
      const curCount = blog.count;

      if (parsedDataMap[curYear]) {
        parsedDataMap[curYear].months.push({
          month: curMonth,
          count: curCount,
        });
        parsedDataMap[curYear].totalCount += curCount;
      } else {
        parsedDataMap[curYear] = {
          year: curYear,
          months: [
            {
              month: curMonth,
              count: curCount,
            },
          ],
          totalCount: curCount,
        };
      }
    });

    parsedPastArticlesData.value = Object.values(parsedDataMap);
    return orderBy(parsedPastArticlesData.value, 'year', 'desc');
  };

  const fetchBlogArticles = async ({
    id,
    count,
    listDisplayFlag,
    detailDisplayFlag,
    isCurrentlyPublished,
    cancelToken,
  }: {
    id: string;
    count: number;
    listDisplayFlag: number;
    detailDisplayFlag: number;
    isCurrentlyPublished: boolean;
    cancelToken?: CancelToken;
  }) => {
    return await store.dispatch('blog/master/fetchBlogArticles', {
      shopId: id,
      sort: selectedSortOption.value,
      year: selectedYearOption.value,
      month: selectedMonthOption.value !== '' ? selectedMonthOption.value.padStart(2, '0') : selectedMonthOption.value,
      count,
      listDisplayFlag,
      detailDisplayFlag,
      isCurrentlyPublished,
      cancelToken,
    });
  };

  // この店のスタッフレビューデータ取得
  const fetchStaffList = async (shopDetail: ECSchema<'saleslocation.ShowResponse'> | undefined) => {
    if (!shopDetail) return;
    const placeCode = shopDetail.place_code;
    const { items } = await store.dispatch('modules/staff/fetchStaffListByShopCode', {
      shop_code: placeCode,
      pagination: 1,
    });
    staffList.value = items;
  };

  // TODO: 暫定対応
  // 今はどのようにスタッフAPIのためのIDを取得するかとわからないので、一旦スタッフリストから取得
  const staffShopId = computed(() => {
    return staffList.value.length > 0 ? staffList.value[0].shop_id : '';
  });

  return {
    staffList,
    selectedSortOption,
    selectedYearOption,
    selectedMonthOption,
    convertQueryToMap,
    convertMapToQuery,
    fetchPastArticles,
    fetchBlogArticles,
    fetchStaffList,
    staffShopId,
  };
};
